import { useEffect, useState } from "react";
import { Trans } from "react-i18next";
import { useTranslation } from "hooks/use-translation";
import Input from "components/input";
import Button from "components/button";
import PasswordInput from "components/password-input";
import { MainWrapper } from "components/main-wrapper";
import { useStateSearchParam } from "hooks/use-state-search-param";
import ServiceUnavailable from "components/service-unavailable";
import { useLoginCheck } from "hooks/use-login-check";
import PropTypes from "prop-types";

const { REACT_APP_SSO_DOMAIN } = process.env;

export function Login() {
  const { tcommon, tpage } = useTranslation({
    page: "login",
  });

  const { state, searchParams } = useStateSearchParam();

  const getError = () => {
    const urlError = searchParams.get("error");
    if (urlError == undefined) return "";
    return decodeURI(urlError);
  };

  const clearError = () => {
    if (!error) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    params.delete("error");
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}?${params}`,
    );
    setError("");
  };

  const [fields, setFields] = useState({
    username: searchParams.get("email") ?? "",
    password: "",
    device: "",
  });

  const {
    isPasswordLogin,
    setIsPasswordLogin,
    loading: loginCheckLoading,
    onFormSubmit: onEmailFormSubmit,
  } = useLoginCheck(fields.username);

  const shouldDisableSignUp = searchParams.get("disable_sign_up") === "true";

  const [error, setError] = useState(getError());
  const [loading, setLoading] = useState(false);

  const isLoading = loading || loginCheckLoading;
  const isLearnUponError = error.toUpperCase().includes("LEARNUPON");

  const registerURL = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete("error");
    return `/register?${params}`;
  };
  const forgottenPasswordURL = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete("error");
    return `/forgot-password?${params}`;
  };

  const onSubmit = (event) => {
    event.preventDefault();
    setLoading(true);
    event.target.submit();
  };

  useEffect(() => {
    if (state) {
      setFields({ ...fields, device: state["device"] });
    }

    if (window.location.pathname === "/login") {
      window.localStorage.setItem("loginSearchParams", window.location.search);
    }

    if(fields.username != null) {
      onEmailFormSubmit({ preventDefault: () => {} });
    }
  }, []);

  const isGivBackLogin = window.location.search.includes(
    "cloud.givenergy.phoenix",
  );

  if (isLearnUponError) {
    return <ServiceUnavailable service="learn-upon" onCtaClick={clearError} />;
  }

  if (isPasswordLogin) {
    return (
      <LoginWrapper error={error}>
        <form
          action={`${REACT_APP_SSO_DOMAIN}/login${window.location.search}`}
          method="POST"
          encType="multipart/form-data"
          className="space-y-4"
          onSubmit={onSubmit}
        >
          <div className="flex flex-col gap-4 mb-2">
            <Input
              label={tpage("email-address")}
              id="email"
              name="email"
              type="email"
              value={fields.username}
              disabled
            />

            <input type="hidden" name="email" value={fields.username} />

            <PasswordInput
              ref={(ref) => {
                ref?.focus();
              }}
              label={tcommon("password")}
              id="password"
              name="password"
              value={fields.password}
              autoComplete="current-password"
              onChange={(event) =>
                setFields({ ...fields, password: event.target.value })
              }
              required
            />

            <input type="hidden" name="device" value={fields.device} />
          </div>

          <Button type="submit" loading={isLoading}>
            {tpage("login")}
          </Button>

          <div className="flex flex-row items-center justify-between gap-4">
            <span
              className="underline text-xs hover:text-zinc-500 dark:hover:text-zinc-100 cursor-pointer"
              onClick={() => {
                setIsPasswordLogin(false);
                setFields({ ...fields, password: "" });
                clearError();
              }}
            >
              {tpage("go-back")}
            </span>

            <a href={forgottenPasswordURL()}>{tpage("recover-cta")}</a>
          </div>
        </form>
      </LoginWrapper>
    );
  }

  return (
    <LoginWrapper error={error}>
      <form
        className="space-y-4"
        onSubmit={(event) => {
          clearError();
          onEmailFormSubmit(event);
        }}
      >
        <Input
          label={tpage("email-address")}
          id="email"
          name="email"
          type="email"
          placeholder="new.user@givenergy.co.uk"
          autoComplete="email"
          value={fields.username}
          onChange={(event) =>
            setFields({ ...fields, username: event.target.value })
          }
          required
        />

        <PasswordInput
          className="hidden"
          id="hidden-password"
          name="hidden-password"
          value={fields.password}
          autoComplete="current-password"
          onChange={(event) =>
            setFields({ ...fields, password: event.target.value })
          }
        />

        <Button type="submit" loading={isLoading}>
          {tpage("continue-with-email")}
        </Button>

        {!shouldDisableSignUp && (
          <div className="flex justify-center">
            <a href={registerURL()}>{tpage("register-cta")}</a>
          </div>
        )}

        {isGivBackLogin && (
          <div className="text-center">
            <span>
              If you are having trouble signing in,{" "}
              <a href="https://givenergy.cloud/sso/manage" className="text-sm">
                try migrating to SSO
              </a>
            </span>
          </div>
        )}
      </form>
    </LoginWrapper>
  );
}

function LoginWrapper({ children, error }) {
  const { translate, tpage } = useTranslation({
    page: "login",
  });

  return (
    <MainWrapper
      description={tpage("sso-login-description")}
    >
      <div className="flex flex-col gap-4 w-full">
        {children}

        {error && (
          <p className="max-w-full text-red-500/80 dark:text-red-500">
            <Trans
              t={translate}
              i18nKey={"page.login.invalid-credentials-cta"}
            />
          </p>
        )}
      </div>
    </MainWrapper>
  );
}

LoginWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  error: PropTypes.string,
};
